import { inject, injectable } from 'inversify';
import { autorun, makeAutoObservable } from 'mobx';
import {
  AccountListLastMonth,
  AccountReport,
  AccountReportCategory,
  AccountReportLastMonth
} from '@shared/types/osi/accountReports';
import { AccountReportsModel } from '@mobx/services/accountReports/model';
import logger from 'js-logger';
import { notifications } from '@shared/utils/notifications';
import { clearPersistedStore, makePersistable } from 'mobx-persist-store';
import { AccountReportsModule } from '@shared/types/mobx/services/accountReports';
import { queryClient } from '@shared/api/reactQuery';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import { type AuthService, AuthServiceIdentifier } from '@shared/types/mobx/services/auth';
import {
  getGetApiAccountReportsCategoriesQueryOptions,
  getGetApiAccountReportsListListIdQueryOptions,
  getGetApiAccountReportsOsiOsiIdPrevMonthStatusQueryOptions
} from '@/shared/api/orval/account-reports/account-reports';

interface OsiInfo {
  name: string;
  idn: string;
  unionTypeId: number | null;
}

@injectable()
export class AccountReportsModuleImpl implements AccountReportsModule {
  allReports: AccountReport[] | null = null;

  lastMonthReport: AccountReportLastMonth | null = null;

  categories: AccountReportCategory[] | null = null;

  isLoading = false;

  constructor(
    private model: AccountReportsModel,
    @inject(OsiServiceIdentifier) private osiModule: OsiService,
    @inject(AuthServiceIdentifier) private authModule: AuthService
  ) {
    makeAutoObservable(this);
    // не персистим allReports — иначе stale-кэш перекрывает свежий ответ API
    // (новые счета OsiAccount не появляются в UI после их добавления).
    // Плановое решение 2026-04-22. lastMonthReport оставлен — используется виджетом главной ОСИ.
    makePersistable(this, { name: 'AccountReportsModule', properties: ['lastMonthReport'] });

    // OSI-796: каталог категорий грузим ТОЛЬКО у авторизованного пользователя.
    // Раньше loadCategoriesCatalog() вызывался безусловно в конструкторе, а сам стор с dfe04f50
    // резолвится на верхнем уровне @mobx/root — значит GET /core/api/AccountReports/categories
    // уходил сразу при загрузке бандла у НЕавторизованного пользователя, получал 401, падал на
    // response.result и повторялся ещё 2 раза (retry) — до трёх тостов «Сессия истекла» подряд.
    // Образец защиты — DictionaryModule: autorun + проверка authModule.userData?.id.
    // Категории нужны только в авторизованной зоне /osi/* (редактор отчёта по счетам),
    // плюс есть страховка ensureCategoriesLoaded() при открытии редактора.
    autorun(() => {
      if (!this.authModule.userData?.id) return;

      this.loadCategoriesCatalog();
    });
  }

  get accounts(): AccountListLastMonth[] {
    return this.lastMonthReport?.lists ?? [];
  }

  get osiInfo(): OsiInfo {
    return {
      name: this.osiModule.osiInfo?.name || '',
      idn: this.osiModule.osiInfo?.idn || '',
      unionTypeId: this.osiModule.osiInfo?.unionTypeId || null
    };
  }

  loadCategoriesCatalog = async () => {
    // Каталог категорий грузится один раз, но при транзиентном сбое сети categories
    // оставался null навсегда, и ячейки «Категория» в редакторе молча исчезали.
    // Делаем до 2 повторных попыток (итого 3) с задержкой, прежде чем сдаться.
    const maxAttempts = 3;
    const retryDelayMs = 800;

    try {
      this.isLoading = true;

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
          const response = await queryClient.fetchQuery(getGetApiAccountReportsCategoriesQueryOptions());

          this.categories = response.result ?? [];
          return; // успех — каталог заполнен, выходим
        } catch (e) {
          logger.error(e);

          // Ещё остались попытки — ждём и пробуем снова
          if (attempt < maxAttempts) {
            await new Promise((r) => setTimeout(r, retryDelayMs));
            continue;
          }

          // Все попытки исчерпаны — categories остаётся null, уведомляем пользователя
          notifications.error();
        }
      }
    } finally {
      this.isLoading = false;
    }
  };

  // Дозагрузка каталога при открытии редактора отчёта: если первичная загрузка в
  // конструкторе упала (categories === null) и загрузка не идёт — запускаем её снова.
  ensureCategoriesLoaded = () => {
    if (this.categories === null && !this.isLoading) {
      this.loadCategoriesCatalog();
    }
  };

  loadReports = async () => {
    await this.loadAllReports();

    await this.loadLastMonthReport();
  };

  loadAllReports = async () => {
    try {
      if (!this.osiModule.osiInfo?.id) return;

      this.isLoading = true;
      // защита от рендера stale-данных между hydrate mobx-persist и fetch
      this.allReports = null;
      this.allReports = (await this.model.loadOsiAccountReports(
        this.osiModule.osiInfo.id
      )) as unknown as AccountReport[];
    } catch (e) {
      notifications.error(e);
      logger.error(e);

      if (this.allReports?.length) {
        this.allReports = [];
      }
    } finally {
      this.isLoading = false;
    }
  };

  loadLastMonthReport = async () => {
    try {
      if (!this.osiModule.osiInfo?.id) return;
      this.isLoading = true;

      const response = await queryClient.fetchQuery(
        getGetApiAccountReportsOsiOsiIdPrevMonthStatusQueryOptions(this.osiModule.osiInfo.id, {
          query: {
            staleTime: 0,
            gcTime: 0
          }
        })
      );

      this.lastMonthReport = response.result ?? null;
    } catch (e: any) {
      if (!e.includes('Отчет не найден')) {
        logger.error(e);
        notifications.error();
      }
      this.lastMonthReport = null;
    } finally {
      this.isLoading = false;
    }
  };

  loadListById = async (listId: number) => {
    try {
      this.isLoading = true;
      const response = await queryClient.fetchQuery(
        getGetApiAccountReportsListListIdQueryOptions(listId, {
          query: {
            staleTime: 0,
            gcTime: 0
          }
        })
      );
      return response.result;
    } catch (e) {
      notifications.error(e);
    } finally {
      this.isLoading = false;
    }
  };

  updateLastReportData = async () => {
    this.loadLastMonthReport();
  };

  // Чистим persist сессии: сбрасываем персистируемый lastMonthReport (и in-memory поля) к initial,
  // затем удаляем localStorage-ключ. Иначе отчёт прошлого месяца протекает между входами.
  clearSession = async () => {
    this.allReports = null;
    this.lastMonthReport = null;
    await clearPersistedStore(this);
  };
}
